import React, { useContext, useEffect } from "react";
import { useState } from "react";
import {
  TextField,
  Button,
  Grid,
  Paper,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  FormControl,
  InputLabel,
  Modal,
  Box,
  FormControlLabel,
  Checkbox,
  Autocomplete,
} from "@mui/material";
import IconButton from "@mui/material/IconButton"; // se você usa imports nomeados, apenas adicione IconButton entre os imports do MUI
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import ShareIcon from "@mui/icons-material/Share";
import { CepService } from "../../Services/CepService";
import { AppProvider } from "../../ContextApi/AppProvider";
import ImprimirPedido from "./ImprimirPedido";
import { pdf } from "@react-pdf/renderer";
import { ClienteService } from "../../Services/ClienteService";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import IProduto from "../Shared/Interfaces/Produto";
import { ProdutosServices } from "../../Services/ProdutosService";
import type { KitResponseDto } from "../Shared/Interfaces/Kit";
import PedidoModalResumo from "./PedidoModalResumo";
import ItensPedido from "../Shared/Interfaces/ItensPedido";
import {
  formatToBRL,
  limparMascaraDocumento,
  cpfApplyMask,
  cnpjApplyMask,
} from "../../Utils/utils";
import {
  Cliente,
  FormPesquisarCliente,
  initialStateCliente,
} from "../Shared/Interfaces/Cliente";
import Pedido, { FiltroPesquisarPedido } from "../Shared/Interfaces/Pedido";
import { PedidoService } from "../../Services/PedidoService";
import { useNavigate, useParams } from "react-router-dom";
import DefaultLoader from "../Shared/DefaultLoader";
import IPagamentosPedido from "../Shared/Interfaces/PagamentosPedido";
import { CadastroService } from "../../Services/CadastrosService";
import { TipoCadastro } from "../Shared/Enums/TipoCadastro";
import DataItem from "../Shared/Interfaces/DataItem";
import colors from "../../theme/colors";
import { KitsService } from "../../Services/KitsService";

const cepService = new CepService();
const clienteService = new ClienteService();
const produtoService = new ProdutosServices();
const kitsService = new KitsService();
const pedidoService = new PedidoService();
const cadastroService = new CadastroService();

const initialStatePedido: Pedido = {
  id: 0,
  valorTotal: 0, 
  quantidadeParcelas: 0,
  observacoes: "",
  dataCriacao: "",
  formaPagamento: "",
  cliente: initialStateCliente,
  itensPedido: [],
  pagamentosPedido: [],
};

/** Número do endereço na API é int; string vazia quebra a desserialização — envia 0. */
function clienteNumeroParaApi(
  value: string | number | null | undefined
): number {
  if (value == null) return 0;
  const s = String(value).trim();
  if (s === "") return 0;
  const n = parseInt(s, 10);
  return Number.isFinite(n) ? n : 0;
}

const styleModal = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  width: { xs: "95vw", sm: 750 },
  height: "auto",
  transform: "translate(-50%, -50%)",
  maxWidth: "90vw",
  maxHeight: "90vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: { xs: 2, sm: 4 },
};

const stylePdfActionsModal = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "92vw", sm: 420 },
  maxWidth: 420,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 2,
  p: { xs: 2, sm: 3 },
};

export default function GerarPedidos() {
  const navigate = useNavigate();
  const { pedidoId } = useParams();
  const pedidoIdNumber = Number(pedidoId);

  /* Controles da modal de resumo */
  const [openModal, setOpenModal] = useState(false);
  const [openPdfActionsModal, setOpenPdfActionsModal] = useState(false);
  const [pedidoConcluido, setPedidoConcluido] = useState<Pedido | null>(null);
  const [navigateAfterPdfActions, setNavigateAfterPdfActions] = useState(false);
  const handleOpenModal = () => {
    const dataAtual = new Date();
    
    // Garantir que o documento está limpo e sincronizado
    const clienteAtualizado = {
      ...cliente,
      documento: limparMascaraDocumento(cliente.documento)
    };

    // Validar se o nome do cliente está preenchido
    if (!clienteAtualizado.nome || clienteAtualizado.nome.trim() === "") {
      contextApi.showMessage("Nome do cliente é obrigatório", true, "warning");
      return;
    }

    // Remover itemId antes de enviar para o backend (é apenas um identificador temporário do frontend)
    const itensPedidoLimpos = itensPedido.map((item) => {
      const { itemId, ...itemSemId } = item as any;
      return itemSemId as ItensPedido;
    });
    
    const pedidoNovo: Pedido = {
      id: 0,
      valorTotal: itensPedido.reduce((acc, item) => acc + item.valorTotal, 0),
      quantidadeParcelas: 0,
      observacoes: pedido.observacoes || "",
      dataCriacao: dataAtual.toISOString(),
      cliente: clienteAtualizado,
      formaPagamento: "",
      itensPedido: itensPedidoLimpos,
      pagamentosPedido: pagamentosPedido,
    };

    setPedido(pedidoNovo);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setPagamentosPedido([]);
    setOpenModal(false);
  };

  const contextApi = useContext(AppProvider);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [produtos, setProdutos] = React.useState<IProduto[]>([]);
  const [kits, setKits] = React.useState<KitResponseDto[]>([]);
  const [itensPedido, setItensPedido] = React.useState<ItensPedido[]>([]);
  const [pagamentosPedido, setPagamentosPedido] = React.useState<
    IPagamentosPedido[]
  >([]);
  const [pedido, setPedido] = React.useState<Pedido>(initialStatePedido);
  const [cliente, setCliente] = React.useState<Cliente>(initialStateCliente);
  const [clienteDocumento, setClienteDocumento] = React.useState("");
  const [canaisDeComunicacao, setCanaisDeComunicacao] = React.useState<DataItem[]>([]);

  const [produtoSelecionado, setProdutoSelecionado] = useState<IProduto>();
  const [kitSelecionado, setKitSelecionado] = useState<KitResponseDto>();
  const [quantidadeProduto, setQuantidadeProduto] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingCliente, setLoadingCliente] = useState(false);
  const [loadingCep, setLoadingCep] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [clientesEncontrados, setClientesEncontrados] = useState<Cliente[]>([]);
  const [selectedClients, setSelectedClients] = useState<Cliente[]>([]); // Armazena clientes selecionados
  // --- Novos estados para edição inline do preço ---
  const [editingPriceId, setEditingPriceId] = useState<string | null>(null);
  const [editingPriceValue, setEditingPriceValue] = useState<string>("");
  const [itemIdCounter, setItemIdCounter] = useState(0);
  // edição de lote e série para pneus
  const [editingLoteId, setEditingLoteId] = useState<string | null>(null);
  const [editingLoteValue, setEditingLoteValue] = useState<string>("");
  const [editingSerieId, setEditingSerieId] = useState<string | null>(null);
  const [editingSerieValue, setEditingSerieValue] = useState<string>("");

  type ProdutoKitBuscaResponse = {
    produtos: IProduto[];
    kits: KitResponseDto[];
  };

  type ProdutoKitOption =
    | { kind: "PRODUTO"; id: number; label: string; produto: IProduto }
    | { kind: "KIT"; id: number; label: string; kit: KitResponseDto };

  const [produtoBusca, setProdutoBusca] = useState("");
  const [produtoOptions, setProdutoOptions] = useState<ProdutoKitOption[]>([]);
  const [loadingProdutoBusca, setLoadingProdutoBusca] = useState(false);
  const [produtoOptionSelecionada, setProdutoOptionSelecionada] =
    useState<ProdutoKitOption | null>(null);


  // --- Funções para edição inline do preço ---
  const moneyDigitsToBRL = (digits: string) => {
    const cents = digits ? parseInt(digits, 10) : 0;
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  };

  const moneyStringToNumber = (value: string) => {
    const digits = value.replace(/\D/g, "");
    const cents = digits ? parseInt(digits, 10) : 0;
    return cents / 100;
  };

const startEditPrice = (itemId: string) => {
  const item = itensPedido.find((i) => (i as any).itemId === itemId);
  if (!item) return;
  setEditingPriceId(itemId);
  const cents = Math.round((item.preco ?? 0) * 100).toString();
  setEditingPriceValue(moneyDigitsToBRL(cents));
};

const cancelEditPrice = () => {
  setEditingPriceId(null);
  setEditingPriceValue("");
};

const saveEditPrice = (itemId: string) => {
  const parsed = moneyStringToNumber(editingPriceValue);

  setItensPedido((prev) =>
    prev.map((item) =>
      (item as any).itemId === itemId
        ? {
            ...item,
            preco: parsed,
            valorTotal: parsed * (item.quantidade || 0),
          }
        : item
    )
  );

  setEditingPriceId(null);
  setEditingPriceValue("");
};

  const handleClienteDocumentoChange = (value: string) => {
    const onlyNumbers = limparMascaraDocumento(value);

    if (onlyNumbers.length === 11) {
      const cpf = cpfApplyMask(onlyNumbers);
      setClienteDocumento(cpf);
      handleInputCliente("documento", cpf);
      return;
    }

    if (onlyNumbers.length === 14) {
      const cnpj = cnpjApplyMask(onlyNumbers);
      setClienteDocumento(cnpj);
      handleInputCliente("documento", cnpj);
      return;
    }

    setClienteDocumento(onlyNumbers);
    handleInputCliente("documento", onlyNumbers);
  };

    const handlePastecep = (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault(); // Prevent the default paste action
      const pastedData = e.clipboardData.getData('Text');
      const cleanedData = limparMascaraDocumento(pastedData);
      handleInputCliente("cep", cleanedData);
    };

  const handleInputCliente = (fieldName: keyof Cliente, value: string) => {
    setCliente((prevCliente) => ({
      ...prevCliente,
      [fieldName]: value,
    }));
  };

  const addItemPedido = (item: ItensPedido) => {
    setItensPedido((prevItens) => [...prevItens, item]);
    setQuantidadeProduto(0);
    setProdutoOptionSelecionada(null);
    setProdutoSelecionado(undefined);
    setKitSelecionado(undefined);
    setProdutoBusca("");
    setProdutoOptions([]);
  };

  const resetFormularioPedido = () => {
    setCliente(initialStateCliente);
    setClienteDocumento("");
    setPedido(initialStatePedido);
    setPagamentosPedido([]);
    setItensPedido([]);
    BuscarProdutos();
  };

  const abrirModalPdfAposConcluir = (pedidoFinal: Pedido, shouldNavigate: boolean) => {
    setPedidoConcluido(pedidoFinal);
    setNavigateAfterPdfActions(shouldNavigate);
    setOpenModal(false);
    setOpenPdfActionsModal(true);
  };

  const handleClosePdfActionsModal = () => {
    setOpenPdfActionsModal(false);
    setPedidoConcluido(null);
    if (navigateAfterPdfActions) {
      setNavigateAfterPdfActions(false);
      navigate("../consultarpedidos");
    }
  };

  const ConcluirPedido = (valorTotal: number) => {
    setLoading(true);

    if (pedido.valorTotal != valorTotal) {
      pedido.valorTotal = valorTotal;
    }
/*
    if (pedido.cliente.documento == "") {
      contextApi.showMessage(
        "Documento do cliente é obrigatório",
        true,
        "warning"
      );
      setLoading(false);
      return;
    }
*/
    const pedidoBody: Pedido = {
      ...pedido,
      cliente: {
        ...pedido.cliente,
        numero: clienteNumeroParaApi(pedido.cliente.numero),
      },
    };

    if (pedidoIdNumber > 0) {
      pedidoBody.id = pedidoIdNumber;
      pedidoService
        .putPedido(`/api/Pedidos/atualizar/${pedidoId}`, pedidoBody)
        .then((response) => {
          if (response.status == 204) {
            resetFormularioPedido();
            contextApi.showMessage(
              `Pedido #000${pedidoIdNumber} Atualizado com Sucesso!`,
              true,
              "success"
            );
            setLoading(false);
            abrirModalPdfAposConcluir(
              { ...pedidoBody, id: pedidoIdNumber },
              true
            );
          }
        })
        .catch((error) => {
          let errorMessage = "";
          if (error?.response?.data) {
            if (typeof error.response.data === "string") {
              errorMessage = error.response.data;
            } else if (error.response.data?.message) {
              errorMessage = error.response.data.message;
            }
          }
          if (errorMessage) {
            contextApi.showMessage(errorMessage, true, "error");
          }
          setLoading(false);
        });
    } else {
      pedidoService
        .postPedido("/api/Pedidos/adicionar", pedidoBody)
        .then((response) => {
          if (response.status == 201) {
            let idPedido = response.data.id;
            resetFormularioPedido();
            contextApi.showMessage(
              `Pedido #000${idPedido} gerado com Sucesso!`,
              true,
              "success"
            );
            setLoading(false);
            abrirModalPdfAposConcluir({ ...pedidoBody, id: idPedido }, false);
          }
        })
        .catch((error) => {
          let errorMessage = "";
          if (error?.response?.data) {
            if (typeof error.response.data === "string") {
              errorMessage = error.response.data;
            } else if (error.response.data?.message) {
              errorMessage = error.response.data.message;
            }
          }
          if (errorMessage) {
            contextApi.showMessage(errorMessage, true, "error");
          }
          setLoading(false);
        });
    }
  };

  const BuscarCep = () => {
    setLoadingCep(true);
    if (cliente.cep == "") {
      contextApi.showMessage("Informe um cep válido!", true, "error");
      setLoadingCep(false);
    } else {
      cepService
        .getCep(`https://viacep.com.br/ws/${cliente.cep}/json/`)
        .then((response) => {
          if (response.data.erro) {
            contextApi.showMessage("Cep não encontrado", true, "warning");
            setLoadingCep(false);
          }
          handleInputCliente("bairro", response.data.bairro);
          handleInputCliente("complemento", response.data.complemento);
          handleInputCliente("cidade", response.data.localidade);
          handleInputCliente("logradouro", response.data.logradouro);
          handleInputCliente("estado", response.data.uf);
          setLoadingCep(false);
        })
        .catch((error) => {
          let errorMessage = "";
          if (error?.data?.erro) {
            errorMessage = error.data.erro;
          } else if (error?.response?.data) {
            if (typeof error.response.data === "string") {
              errorMessage = error.response.data;
            } else if (error.response.data?.message) {
              errorMessage = error.response.data.message;
            }
          }
          if (errorMessage) {
            contextApi.showMessage(errorMessage, true, "error");
          }
          setLoadingCep(false);
        });
    }
  };

  const handleDelete = (itemId: string) => {
    const updatedItensPedido = itensPedido.filter(
      (item) => (item as any).itemId !== itemId
    );
    setItensPedido(updatedItensPedido);
  };

  const BuscarClientePorDocumento = () => {
    setLoadingCliente(true);
    setCliente(initialStateCliente);
    setSelectedClients([]);
    const filtroPesquisa: FormPesquisarCliente = {
      nome: cliente.nome,
      documento: limparMascaraDocumento(clienteDocumento),
    };
    if (clienteDocumento == "" && cliente.nome == "") {
      setLoadingCliente(false);
      contextApi.showMessage(
        "Informe um documento ou nome válido!",
        true,
        "error"
      );
    } else {
      clienteService
        .buscarPor(filtroPesquisa)
        .then((response) => {
          if (response.data.length === 0) {
            contextApi.showMessage("Nenhum cliente encontrado!", true, "info");
          } else if (response.data.length === 1) {
            setCliente(response.data[0]);
            handleClienteDocumentoChange(response.data[0].documento);
            setLoadingCliente(false);
          } else {
            setLoadingCliente(false);
            setClientesEncontrados(response.data);
            setIsModalOpen(true); // Abrindo a modal para seleção
          }
        })
        .catch((error) => {
          handleInputCliente("documento", clienteDocumento);
          let errorMessage = "";
          if (error?.response?.data) {
            if (typeof error.response.data === "string") {
              errorMessage = error.response.data;
            } else if (error.response.data?.message) {
              errorMessage = error.response.data.message;
            }
          }
          if (errorMessage) {
            contextApi.showMessage(errorMessage, true, "error");
          }
          setLoadingCliente(false);
        });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
  };

  const openPDFInNewTab = async (pedidoData: Pedido) => {
    const blob = await pdf(
      <ImprimirPedido data={pedidoData} numeroPedido={`#000${pedidoData.id}`} />
    ).toBlob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  };

  const generatePedidoPdfFile = async (pedidoData: Pedido) => {
    const blob = await pdf(
      <ImprimirPedido data={pedidoData} numeroPedido={`#000${pedidoData.id}`} />
    ).toBlob();

    return new File([blob], `pedido-${pedidoData.id}.pdf`, {
      type: "application/pdf",
    });
  };

  const handleSharePedidoPdf = async (pedidoData: Pedido) => {
    try {
      const file = await generatePedidoPdfFile(pedidoData);
      const shareData: ShareData = {
        files: [file],
        title: `Pedido #${pedidoData.id}`,
        text: `PDF do pedido #${pedidoData.id}`,
      };

      if (navigator.canShare?.(shareData)) {
        await navigator.share(shareData);
        return;
      }

      const url = URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.name;
      link.click();
      URL.revokeObjectURL(url);
      contextApi.showMessage(
        "Seu navegador não suporta compartilhar arquivos. O PDF foi baixado.",
        true,
        "info"
      );
    } catch (error: any) {
      if (error?.name === "AbortError") {
        return;
      }
      contextApi.showMessage(
        "Não foi possível compartilhar o PDF do pedido.",
        true,
        "error"
      );
    }
  };

  const adicionarProduto = () => {
    if ((produtoSelecionado == null && kitSelecionado == null) || quantidadeProduto == 0) {
      contextApi.showMessage(
        "Informe o Produto e a Quantidade Corretamente!",
        true,
        "warning"
      );
      return;
    }
/*
    if (itensPedido.find((item) => item.idProduto == produtoSelecionado.id)) {
      contextApi.showMessage("Produto Já adicionado!", true, "warning");
      return;
    }
*/
    const isKit = kitSelecionado != null;
    const valor = isKit ? Number(kitSelecionado?.valor) || 0 : (produtoSelecionado?.valor ?? 0);
    const descricao = isKit
      ? `[KIT] ${kitSelecionado?.nome}`
      : `${produtoSelecionado?.descricao} - ${produtoSelecionado?.medida} - ${produtoSelecionado?.marca}`;

    const newItem: ItensPedido & { itemId: string } = {
      idProduto: isKit ? 0 : (produtoSelecionado?.id ?? 0),
      idKit: isKit ? (kitSelecionado?.id ?? 0) : 0,
      codigo: isKit ? (kitSelecionado?.id ?? 0) : (produtoSelecionado?.codigo ?? 0),
      descricao: descricao,
      quantidade: quantidadeProduto,
      preco: valor,
      valorTotal: quantidadeProduto * valor,
      lote: "",
      serie: "",
      itemId: `item-${Date.now()}-${itemIdCounter}`, // ID único para cada item
    };

    setItemIdCounter((prev) => prev + 1);
    addItemPedido(newItem);
  };
  interface Column {
    id:
      | "idProduto"
      | "descricao"
      | "quantidade"
      | "preco"
      | "lote"
      | "serie"
      | "valorTotal";
    label: string;
    minWidth?: number;
    align?: "right";
    format?: (value: number) => string;
  }

  const columns: readonly Column[] = [
    { id: "idProduto", label: "Id", minWidth: 20 },
    { id: "descricao", label: "Produto", minWidth: 150 },
    { id: "quantidade", label: "Quantidade", minWidth: 50 },
    {
      id: "preco",
      label: "Preco",
      minWidth: 100,
      format: (value: number) => formatToBRL(value),
    },
    /*
    { id: "lote", label: "Dote", minWidth: 80 },
    { id: "serie", label: "Série", minWidth: 80 },
    */
    {
      id: "valorTotal",
      label: "Total",
      minWidth: 100,
      format: (value: number) => formatToBRL(value),
    },
  ];

  const BuscarProdutos = async () => {
    try {
      const [prod, kitsResp] = await Promise.all([
        produtoService.getAll("/api/Produtos/Buscar"),
        kitsService.buscar(),
      ]);
      setProdutos(prod.data || []);
      setKits(Array.isArray(kitsResp.data) ? kitsResp.data : []);
    } catch (e) {
      contextApi.showMessage("Error ao buscar produtos", true, "error");
    }
  };

  const BuscarProdutosPorDescricao = async (descricao: string) => {
    const d = (descricao || "").trim();
    if (d.length < 3) {
      setProdutos([]);
      setKits([]);
      setProdutoOptions([]);
      return;
    }

    setLoadingProdutoBusca(true);
    try {
      const qs = new URLSearchParams({ descricao: d });
      const resp = await produtoService.getAll(
        `/api/Produtos/BuscarPorDescricao?${qs.toString()}`
      );

      const data = (resp.data || {}) as ProdutoKitBuscaResponse;
      const produtosResp = Array.isArray(data.produtos) ? data.produtos : [];
      const kitsResp = Array.isArray(data.kits) ? data.kits : [];

      setProdutos(produtosResp);
      setKits(kitsResp);

      const options: ProdutoKitOption[] = [
        ...kitsResp.map((k) => ({
          kind: "KIT" as const,
          id: k.id,
          label: `[KIT] ${k.nome} - ${formatToBRL(Number(k.valor) || 0)}`,
          kit: k,
        })),
        ...produtosResp.map((p) => ({
          kind: "PRODUTO" as const,
          id: p.id,
          label: `${p.descricao} - ${p.medida} - ${p.marca} - Estoque: ${p.quantidade}`,
          produto: p,
        })),
      ];
      setProdutoOptions(options);
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 404) {
        setProdutos([]);
        setKits([]);
        setProdutoOptions([]);
      } else {
        const data = error?.response?.data;
        const msg =
          typeof data === "string"
            ? data
            : data && typeof data === "object"
              ? data.message || JSON.stringify(data)
              : error?.message || "Ocorreu um erro interno. Tente novamente mais tarde.";
        contextApi.showMessage(msg, true, "error");
        setProdutos([]);
        setKits([]);
        setProdutoOptions([]);
      }
    } finally {
      setLoadingProdutoBusca(false);
    }
  };

  const getCanaisDeComunicacao = async () => {
    try {
      const response = await cadastroService.getAll(
        `/api/Cadastros/Buscar?tipoCadastro=${TipoCadastro.CanaisDeComunicacao}`
      );
      setCanaisDeComunicacao(response.data || []);
    } catch (e) {
      console.error("Erro ao buscar Canais de Comunicação:", e);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  useEffect(() => {
    BuscarProdutos();
    getCanaisDeComunicacao();
  }, []);

  useEffect(() => {
    const d = (produtoBusca || "").trim();
    if (d.length < 3) {
      setLoadingProdutoBusca(false);
      setProdutoOptions([]);
      setProdutos([]);
      setKits([]);
      setProdutoSelecionado(undefined);
      setKitSelecionado(undefined);
      setProdutoOptionSelecionada(null);
      return;
    }

    const t = window.setTimeout(() => {
      BuscarProdutosPorDescricao(d);
    }, 300);

    return () => window.clearTimeout(t);
  }, [produtoBusca]);

  useEffect(() => {
    if (pedidoId) {
      const filtroPesquisa: FiltroPesquisarPedido = {
        idPedido: parseInt(pedidoId),
        nomeCliente: "",
        dataInicial: null,
        dataFinal: null,
      };
      pedidoService
        .buscarPor(filtroPesquisa)
        .then((response) => {
          setPedido(response.data[0]);
          setCliente(response.data[0].cliente);
          // Adicionar itemId único para cada item ao carregar do backend
          const itensComId = response.data[0].itensPedido.map((item: ItensPedido, index: number) => ({
            ...item,
            itemId: `item-${Date.now()}-${index}-${item.idProduto}`,
          }));
          setItensPedido(itensComId);
          setClienteDocumento(response.data[0].cliente.documento);
          setPagamentosPedido(response.data[0].pagamentosPedido);
        })
        .catch((error) => {
          let errorMessage = "";
          if (error?.response?.data) {
            if (typeof error.response.data === "string") {
              errorMessage = error.response.data;
            } else if (error.response.data?.message) {
              errorMessage = error.response.data.message;
            }
          }
          if (errorMessage) {
            contextApi.showMessage(errorMessage, true, "error");
          }
        });
    }
  }, [pedidoId]);

  const handleCheckboxChange = (cliente: Cliente, checked: boolean) => {
    // Seleção única: ao marcar um, substitui o anterior.
    if (checked) {
      setSelectedClients([cliente]);
      return;
    }
    setSelectedClients([]);
  };

  const handleConfirmSelection = () => {
    if (selectedClients.length > 1) {
      contextApi.showMessage(
        "Só é possível selecionar um cliente",
        true,
        "error"
      );
      return;
    }

    setCliente(selectedClients[0]);
    setClienteDocumento(selectedClients[0].documento);
    setIsModalOpen(false);
  };

  return (
    <Box sx={{ flexGrow: 1, p: { xs: 1, sm: 2 } }}>
      <Paper sx={{ p: 1 }}>
        <Grid container spacing={1}>
          {/* Seção Dados do Cliente */}
          <Grid item xs={12}>
            <Paper sx={{ p: 0.5, mb: 0.5 }}>
              <h5 style={{ margin: '0 0 10px 0', color: colors.bluePrimary }}>👤 Dados do Cliente</h5>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {/* Primeira linha: CPF - NOME - Busca */}
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                required
                name="documento"
                label="CPF ou CNPJ"
                size="small"
                fullWidth
                disabled={pedidoIdNumber > 0}
                variant="outlined"
                value={clienteDocumento}
                onChange={(e) => handleClienteDocumentoChange(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                required
                name="nome"
                label="Nome Cliente"
                size="small"
                fullWidth
                variant="outlined"
                value={cliente.nome}
                onChange={(e) => handleInputCliente("nome", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1}>
              <Button
                type="submit"
                sx={{ height: "3em", width: "100%" }}
                size="small"
                variant="contained"
                color="primary"
                disabled={pedidoIdNumber > 0 || loadingCliente}
                onClick={() => BuscarClientePorDocumento()}
              >
                <SearchIcon />
                {loadingCliente && (
                  <DefaultLoader
                    height="3em"
                    width="3em"
                    colors={["#1876D2", "#1876D2", "#1876D2", "#1876D2", "#1876D2"]}
                    textInfo="Consultando cliente"
                  />
                )}
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={6}></Grid>
            {/* Segunda linha: Telefone - Email - Onde conheceu */}
            <Grid item xs={12} sm={6} md={1.5}>
              <TextField
                required
                name="telefone"
                label="Telefone"
                size="small"
                fullWidth
                variant="outlined"
                value={cliente.telefone}
                onChange={(e) => handleInputCliente("telefone", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.5}>
              <TextField
                required
                name="email"
                label="E-mail"
                size="small"
                fullWidth
                variant="outlined"
                value={cliente.email}
                onChange={(e) => handleInputCliente("email", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Autocomplete
                fullWidth
                options={canaisDeComunicacao}
                getOptionLabel={(option) => option.descricao}
                size="small"
                value={
                  canaisDeComunicacao.find(
                    (option) => option.descricao === cliente.comoConheceu
                  ) || null
                }
                onChange={(e, newValue) =>
                  handleInputCliente("comoConheceu", newValue?.descricao || "")
                }
                renderInput={(params) => (
                  <TextField {...params} label="Por onde conheceu a loja?" />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}></Grid>
            {/* Terceira linha: CEP e botão de busca */}
            <Grid item xs={12} sm={6} md={1.5}>
              <TextField
                required
                name="cep"
                label="Cep"
                size="small"
                fullWidth
                variant="outlined"
                value={cliente.cep}
                onChange={(e) => handleInputCliente("cep", e.target.value)}
                onPaste={handlePastecep}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1}>
              <Button
                sx={{ height: "3em", width: "100%" }}
                type="submit"
                size="small"
                variant="contained"
                color="primary"
                onClick={() => BuscarCep()}
              >
                <SearchIcon />
              </Button>
              {loadingCep && (
                <DefaultLoader
                  height="3em"
                  width="3em"
                  colors={["#1876D2", "#1876D2", "#1876D2", "#1876D2", "#1876D2"]}
                  textInfo="Consultando cep"
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={9}></Grid>
            
            {/* Quarta linha: Endereço - mais compacta */}
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                required
                name="logradouro"
                label="Logradouro"
                size="small"
                fullWidth
                variant="outlined"
                value={cliente.logradouro}
                onChange={(e) =>
                  handleInputCliente("logradouro", e.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1}>
              <TextField
                required
                name="numero"
                label="Nº"
                size="small"
                fullWidth
                variant="outlined"
                value={cliente.numero}
                onChange={(e) => handleInputCliente("numero", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                required
                name="complemento"
                label="Complemento"
                size="small"
                fullWidth
                variant="outlined"
                value={cliente.complemento}
                onChange={(e) =>
                  handleInputCliente("complemento", e.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                required
                name="bairro"
                label="Bairro"
                size="small"
                fullWidth
                variant="outlined"
                value={cliente.bairro}
                onChange={(e) => handleInputCliente("bairro", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                required
                name="cidade"
                label="Cidade"
                size="small"
                fullWidth
                variant="outlined"
                value={cliente.cidade}
                onChange={(e) => handleInputCliente("cidade", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1}>
              <TextField
                required
                name="estado"
                label="UF"
                size="small"
                fullWidth
                variant="outlined"
                value={cliente.estado}
                onChange={(e) => handleInputCliente("estado", e.target.value)}
              />
            </Grid>

            <PedidoModalResumo
              open={openModal}
              onClose={handleCloseModal}
              onConcluir={ConcluirPedido}
              onObservacoesChange={(value) =>
                setPedido((prev) => ({ ...prev, observacoes: value }))
              }
              pedido={pedido}
              loading={loading}
            />

            <Modal
              open={openPdfActionsModal}
              onClose={handleClosePdfActionsModal}
            >
              <Box sx={stylePdfActionsModal}>
                <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
                  Pedido concluído
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {pedidoConcluido
                    ? `O que deseja fazer com o PDF do pedido #${pedidoConcluido.id}?`
                    : "O que deseja fazer com o PDF do pedido?"}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 1.5,
                  }}
                >
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<PrintIcon />}
                    disabled={!pedidoConcluido}
                    onClick={() => {
                      if (pedidoConcluido) {
                        openPDFInNewTab(pedidoConcluido);
                      }
                    }}
                  >
                    Imprimir
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    fullWidth
                    startIcon={<ShareIcon />}
                    disabled={!pedidoConcluido}
                    onClick={() => {
                      if (pedidoConcluido) {
                        handleSharePedidoPdf(pedidoConcluido);
                      }
                    }}
                  >
                    Compartilhar
                  </Button>
                </Box>
                <Button
                  fullWidth
                  sx={{ mt: 2 }}
                  onClick={handleClosePdfActionsModal}
                >
                  Fechar
                </Button>
              </Box>
            </Modal>
          </Grid>
        </form>
              </Paper>
            </Grid>

            {/* Seção Selecionar Produtos */}
            <Grid item xs={12}>
              <Paper sx={{ p: 0.5, mb: 0.5 }}>
                <h5 style={{ margin: '0 0 10px 0', color: colors.bluePrimary }}>📦 Selecionar Produtos</h5>
                
                {/* Formulário de seleção de produtos */}
                <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Autocomplete
                      fullWidth
                      size="small"
                      options={produtoOptions}
                      loading={loadingProdutoBusca}
                      inputValue={produtoBusca}
                      onInputChange={(_, newInputValue) => setProdutoBusca(newInputValue)}
                      value={produtoOptionSelecionada}
                      getOptionLabel={(opt) => opt.label}
                      isOptionEqualToValue={(a, b) => a.kind === b.kind && a.id === b.id}
                      onChange={(_, opt) => {
                        setProdutoOptionSelecionada(opt || null);
                        if (!opt) {
                          setProdutoSelecionado(undefined);
                          setKitSelecionado(undefined);
                          return;
                        }
                        if (opt.kind === "KIT") {
                          setKitSelecionado(opt.kit);
                          setProdutoSelecionado(undefined);
                        } else {
                          setProdutoSelecionado(opt.produto);
                          setKitSelecionado(undefined);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Digite a descrição (mín. 3 caracteres)"
                          placeholder="Ex.: pneu"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={1}>
                    <TextField
                      required
                      name="quantidade"
                      label="Qtd"
                      size="small"
                      type="number"
                      fullWidth
                      variant="outlined"
                      value={quantidadeProduto}
                      onChange={(e) => setQuantidadeProduto(parseInt(e.target.value))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="large"
                      sx={{ height: '40px' }}
                      onClick={adicionarProduto}
                    >
                      Adicionar
                    </Button>
                  </Grid>
                </Grid>

                {/* Tabela de produtos */}
                <Paper sx={{ width: "100%", overflowX: "auto", boxShadow: 3 }}>
                  <Box sx={{ p: 1, bgcolor: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
                    <h5 style={{ margin: 0, color: colors.bluePrimary }}>📋 Produtos Selecionados</h5>
                  </Box>
                  <TableContainer sx={{ maxHeight: 300 }}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          {columns.map((column) => (
                            <TableCell
                              key={column.id}
                              align={column.align}
                              sx={{ 
                                fontWeight: 'bold',
                                bgcolor: '#f5f5f5',
                                color: colors.bluePrimary
                              }}
                            >
                              {column.label}
                            </TableCell>
                          ))}
                          <TableCell 
                            sx={{ 
                              fontWeight: 'bold',
                              bgcolor: '#f5f5f5',
                              color: colors.bluePrimary
                            }}
                          >
                            Ações
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                      {itensPedido
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((row, index) => {
                          const itemId = (row as any).itemId || `item-${row.idProduto}-${index}`;
                          const produtoRow = (row as any).idKit
                            ? undefined
                            : produtos.find((p) => p.id === row.idProduto);
                          const isPneu =
                            (produtoRow?.tipoProduto || "")
                              .toLowerCase()
                              .includes("pneu");
                          return (
                            <TableRow
                              hover
                              role="checkbox"
                              tabIndex={-1}
                              key={itemId}
                              sx={{
                                bgcolor: index % 2 === 0 ? "#ffffff" : "#fafafa",
                                "&:hover": { bgcolor: "#e3f2fd" },
                              }}
                            >
                              {columns.map((column) => {
                                const value = (row as any)[column.id];
                                // Tratamento especial para o campo preco (editor inline)
                                if (column.id === "preco") {
                                  return (
                                    <TableCell
                                      key={column.id}
                                      align={column.align}
                                      sx={{
                                        borderBottom: "1px solid #e0e0e0",
                                        fontSize: "14px",
                                      }}
                                    >
                                      {editingPriceId === itemId ? (
                                        <>
                                          <TextField
                                            size="small"
                                            value={editingPriceValue}
                                            onChange={(e) =>
                                              setEditingPriceValue(
                                                moneyDigitsToBRL(
                                                  e.target.value.replace(/\D/g, "")
                                                )
                                              )
                                            }
                                            onKeyDown={(e) => {
                                              if (e.key === "Enter") saveEditPrice(itemId);
                                              if (e.key === "Escape") cancelEditPrice();
                                            }}
                                            inputProps={{ inputMode: "numeric" }}
                                            sx={{ width: { xs: "100%", sm: 140 } }}
                                          />
                                          <IconButton
                                            size="small"
                                            onClick={() => saveEditPrice(itemId)}
                                            aria-label="Salvar preço"
                                          >
                                            <SaveIcon fontSize="small" />
                                          </IconButton>
                                          <IconButton
                                            size="small"
                                            onClick={cancelEditPrice}
                                            aria-label="Cancelar edição"
                                          >
                                            <CloseIcon fontSize="small" />
                                          </IconButton>
                                        </>
                                      ) : (
                                        <>
                                          <span>
                                            {column.format && typeof value === "number"
                                              ? column.format(value)
                                              : value}
                                          </span>
                                          <IconButton
                                            size="small"
                                            onClick={() => startEditPrice(itemId)}
                                            sx={{ ml: 1 }}
                                            aria-label="Editar preço"
                                          >
                                            <EditIcon fontSize="small" />
                                          </IconButton>
                                        </>
                                      )}
                                    </TableCell>
                                  );
                                }

                                // campos de lote e série - apenas para pneus
                                if (column.id === "lote") {
                                  return (
                                    <TableCell
                                      key={column.id}
                                      align={column.align}
                                      sx={{
                                        borderBottom: "1px solid #e0e0e0",
                                        fontSize: "14px",
                                      }}
                                    >
                                      {!isPneu ? (
                                        "-"
                                      ) : editingLoteId === itemId ? (
                                        <>
                                          <TextField
                                            size="small"
                                            value={editingLoteValue}
                                            onChange={(e) =>
                                              setEditingLoteValue(e.target.value)
                                            }
                                            onKeyDown={(e) => {
                                              if (e.key === "Enter") {
                                                setItensPedido((prev) =>
                                                  prev.map((item) =>
                                                    (item as any).itemId === itemId
                                                      ? { ...item, lote: editingLoteValue }
                                                      : item
                                                  )
                                                );
                                                setEditingLoteId(null);
                                                setEditingLoteValue("");
                                              }
                                              if (e.key === "Escape") {
                                                setEditingLoteId(null);
                                                setEditingLoteValue("");
                                              }
                                            }}
                                            sx={{ width: { xs: "100%", sm: 100 } }}
                                          />
                                          <IconButton
                                            size="small"
                                            onClick={() => {
                                              setItensPedido((prev) =>
                                                prev.map((item) =>
                                                  (item as any).itemId === itemId
                                                    ? { ...item, lote: editingLoteValue }
                                                    : item
                                                )
                                              );
                                              setEditingLoteId(null);
                                              setEditingLoteValue("");
                                            }}
                                            aria-label="Salvar lote"
                                          >
                                            <SaveIcon fontSize="small" />
                                          </IconButton>
                                          <IconButton
                                            size="small"
                                            onClick={() => {
                                              setEditingLoteId(null);
                                              setEditingLoteValue("");
                                            }}
                                            aria-label="Cancelar edição lote"
                                          >
                                            <CloseIcon fontSize="small" />
                                          </IconButton>
                                        </>
                                      ) : (
                                        <>
                                          <span>{row.lote || ""}</span>
                                          {isPneu && (
                                            <IconButton
                                              size="small"
                                              onClick={() => {
                                                setEditingLoteId(itemId);
                                                setEditingLoteValue(row.lote || "");
                                              }}
                                              sx={{ ml: 1 }}
                                              aria-label="Editar lote"
                                            >
                                              <EditIcon fontSize="small" />
                                            </IconButton>
                                          )}
                                        </>
                                      )}
                                    </TableCell>
                                  );
                                }

                                if (column.id === "serie") {
                                  return (
                                    <TableCell
                                      key={column.id}
                                      align={column.align}
                                      sx={{
                                        borderBottom: "1px solid #e0e0e0",
                                        fontSize: "14px",
                                      }}
                                    >
                                      {!isPneu ? (
                                        "-"
                                      ) : editingSerieId === itemId ? (
                                        <>
                                          <TextField
                                            size="small"
                                            value={editingSerieValue}
                                            onChange={(e) =>
                                              setEditingSerieValue(e.target.value)
                                            }
                                            onKeyDown={(e) => {
                                              if (e.key === "Enter") {
                                                setItensPedido((prev) =>
                                                  prev.map((item) =>
                                                    (item as any).itemId === itemId
                                                      ? { ...item, serie: editingSerieValue }
                                                      : item
                                                  )
                                                );
                                                setEditingSerieId(null);
                                                setEditingSerieValue("");
                                              }
                                              if (e.key === "Escape") {
                                                setEditingSerieId(null);
                                                setEditingSerieValue("");
                                              }
                                            }}
                                            sx={{ width: { xs: "100%", sm: 100 } }}
                                          />
                                          <IconButton
                                            size="small"
                                            onClick={() => {
                                              setItensPedido((prev) =>
                                                prev.map((item) =>
                                                  (item as any).itemId === itemId
                                                    ? { ...item, serie: editingSerieValue }
                                                    : item
                                                )
                                              );
                                              setEditingSerieId(null);
                                              setEditingSerieValue("");
                                            }}
                                            aria-label="Salvar série"
                                          >
                                            <SaveIcon fontSize="small" />
                                          </IconButton>
                                          <IconButton
                                            size="small"
                                            onClick={() => {
                                              setEditingSerieId(null);
                                              setEditingSerieValue("");
                                            }}
                                            aria-label="Cancelar edição série"
                                          >
                                            <CloseIcon fontSize="small" />
                                          </IconButton>
                                        </>
                                      ) : (
                                        <>
                                          <span>{row.serie || ""}</span>
                                          {isPneu && (
                                            <IconButton
                                              size="small"
                                              onClick={() => {
                                                setEditingSerieId(itemId);
                                                setEditingSerieValue(row.serie || "");
                                              }}
                                              sx={{ ml: 1 }}
                                              aria-label="Editar série"
                                            >
                                              <EditIcon fontSize="small" />
                                            </IconButton>
                                          )}
                                        </>
                                      )}
                                    </TableCell>
                                  );
                                }

                                // Render padrão para outros campos
                                return (
                                  <TableCell
                                    key={column.id}
                                    align={column.align}
                                    sx={{
                                      borderBottom: "1px solid #e0e0e0",
                                      fontSize: "14px",
                                    }}
                                  >
                                    {column.format && typeof value === "number"
                                      ? column.format(value)
                                      : value}
                                  </TableCell>
                                );
                              })}
                              <TableCell
                                align="center"
                                sx={{ borderBottom: "1px solid #e0e0e0" }}
                              >
                                <Button
                                  size="small"
                                  color="error"
                                  onClick={() => handleDelete(itemId)}
                                  sx={{ minWidth: "auto", p: 1 }}
                                >
                                  <DeleteIcon />
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    rowsPerPageOptions={[10, 25, 100]}
                    component="div"
                    count={itensPedido.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{ 
                      bgcolor: '#f8f9fa',
                      borderTop: '1px solid #e0e0e0'
                    }}
                  />
                </Paper>
              </Paper>
            </Grid>

            {/* Botão Gerar Pedido - Final */}
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                sx={{ display: "flex", marginLeft: "auto", height: '40px' }}
                onClick={handleOpenModal}
              >
                {parseInt(pedidoId || "0") !== 0
                  ? "Concluir Edição"
                  : "Gerar Pedido"}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Customer selection modal */}
        <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
          <Box sx={styleModal}>
            <Typography variant="h6" style={{ textAlign: "center" }}>
              Selecione um Cliente
            </Typography>
            <div>
              {clientesEncontrados.map((cliente) => (
                <div key={cliente.id}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedClients.some((c) => c.id === cliente.id)}
                        onChange={(event) =>
                          handleCheckboxChange(cliente, event.target.checked)
                        }
                      />
                    }
                    label={`${cliente.nome} - ${cliente.documento} - ${cliente.telefone} - ${cliente.email}`}
                  />
                </div>
              ))}
            </div>
            <div style={{ display: "flex" }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                sx={{ display: "flex" }}
                onClick={() => setIsModalOpen(false)}
              >
                Fechar
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                sx={{ display: "flex", marginLeft: "auto" }}
                disabled={selectedClients.length == 0}
                onClick={handleConfirmSelection}
              >
                Confirmar Selecão
              </Button>
            </div>
          </Box>
        </Modal>
      </Box>
    );
}
